import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Calendar,
  ChevronDown,
  ChevronUp,
  CalendarRange,
  Layers,
  Clock,
  Users,
  MapPin,
  CalendarCheck,
  FileText,
  Trash2,
  Radio,
  Edit,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppSidebar from "@/components/AppSidebar";
import { VerificationReminderBanner } from "@/components/VerificationReminderBanner";
import { IncompleteProfileBanner } from "@/components/IncompleteProfileBanner";
import { TrialBanner } from "@/components/TrialBanner";
import { EmergencyModeBanner } from "@/components/EmergencyModeBanner";
import { DeleteProductionDialog } from "@/components/DeleteProductionDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import OnboardingGuide, { type GuideStep } from "@/components/OnboardingGuide";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import { isAuthSessionError } from "@/lib/authSessionError";
import { endExpiredSession } from "@/lib/endExpiredSession";

interface Briefing {
  id: string;
  festival_name: string;
  location?: string;
  festival_date_start?: string;
  festival_date_end?: string;
  status: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  last_edit_step?: number;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { expanded } = useSidebar();
  const queryClient = useQueryClient();
  const { subscriptionStatus, isSubscriptionLoading, userId } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [draftsOpen, setDraftsOpen] = useState(true);
  const [publishedOpen, setPublishedOpen] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [briefingToDelete, setBriefingToDelete] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showGuide, setShowGuide] = useState(false);

  // Fetch profile for guide state
  const { data: dashboardProfile } = useQuery({
    queryKey: ["profile-guide", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("guide_completed")
        .eq("id", userId)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!userId,
  });

  // Show guide when profile loaded and not completed
  useEffect(() => {
    if (dashboardProfile && !dashboardProfile.guide_completed) {
      setShowGuide(true);
    }
  }, [dashboardProfile]);

  const dashboardGuideSteps: GuideStep[] = [
    {
      targetSelector: '[data-guide="create-production"]',
      title: "Create a production",
      description: "Start here to set up a new festival or event with all details your crew needs.",
    },
    {
      targetSelector: '[data-guide="search"]',
      title: "Search productions",
      description: "Quickly find any of your productions by name.",
    },
    {
      targetSelector: '[data-guide="my-team"]',
      title: "My Team",
      description: "Save your frequent crew members here to speed up adding them to productions.",
    },
    {
      targetSelector: '[data-guide="settings"]',
      title: "Settings",
      description: "Manage your profile, notifications, billing and more.",
    },
    {
      targetSelector: '[data-guide="ai-assistant"]',
      title: "AI Assistant",
      description: "Ask questions about PROduce or get help setting up your production.",
    },
  ];

  const handleGuideComplete = async () => {
    setShowGuide(false);
    if (userId) {
      await supabase.from("profiles").update({ guide_completed: true }).eq("id", userId);
      queryClient.invalidateQueries({ queryKey: ["profile-guide", userId] });
    }
  };

  // Fetch all briefings - DEFENSE IN DEPTH: explicit producer_id filter
  const { data: allBriefings = [], isLoading: briefingsLoading } = useQuery({
    queryKey: ["briefings", userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from("briefings")
        .select("*")
        .eq("producer_id", userId)
        .order("updated_at", { ascending: false });

      if (error) throw error;
      return data as Briefing[];
    },
    enabled: !!userId,
    staleTime: 30000, // Data stays fresh for 30 seconds
  });

  // Fetch crew members for producer's briefings only - DEFENSE IN DEPTH
  const { data: crewData = {}, isLoading: crewLoading } = useQuery({
    queryKey: ["crew-counts", userId],
    queryFn: async () => {
      if (!userId) return {};

      // First get producer's briefing IDs
      const { data: briefingData, error: briefingError } = await supabase
        .from("briefings")
        .select("id")
        .eq("producer_id", userId);

      if (briefingError) throw briefingError;

      const briefingIds = briefingData.map((b) => b.id);
      if (briefingIds.length === 0) return {};

      // Then get crew data for those briefings only
      const { data, error } = await supabase
        .from("crew_members")
        .select("briefing_id, email, briefing_crew_id")
        .in("briefing_id", briefingIds);

      if (error) throw error;

      // Count UNIQUE crew members per briefing. briefing_crew_id is
      // the canonical "person on this briefing" FK (post-migration
      // 20260504240100); lowercased+trimmed email is the legacy
      // fallback. Same priority as the rest of the codebase so the
      // count matches what ProductionDashboard / CrewList show.
      const counts: Record<string, number> = {};
      const seenKeys: Record<string, Set<string>> = {};

      data.forEach(
        (crew: { briefing_id: string; email: string | null; briefing_crew_id?: string | null }) => {
          if (!seenKeys[crew.briefing_id]) {
            seenKeys[crew.briefing_id] = new Set();
          }
          const key = crew.briefing_crew_id
            ? `bc:${crew.briefing_crew_id}`
            : `email:${(crew.email ?? "").toLowerCase().trim()}`;
          if (!seenKeys[crew.briefing_id].has(key)) {
            seenKeys[crew.briefing_id].add(key);
            counts[crew.briefing_id] = (counts[crew.briefing_id] || 0) + 1;
          }
        },
      );
      return counts;
    },
    enabled: !!userId,
    staleTime: 30000, // Data stays fresh for 30 seconds
  });

  const isLoading = briefingsLoading || crewLoading;

  // Filter out briefings without names and separate by status
  const drafts = allBriefings.filter((b) => b.status === "draft" && b.festival_name?.trim());
  const published = allBriefings.filter((b) => b.status === "published" && b.festival_name?.trim());
  // Only show recent projects if they exist and have names
  const recentlyOpened = allBriefings.filter((b) => b.festival_name?.trim()).slice(0, 3);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateRange = (startDate: string, endDate?: string) => {
    if (!endDate || startDate === endDate) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const toggleRowExpanded = (briefingId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(briefingId)) {
        newSet.delete(briefingId);
      } else {
        newSet.add(briefingId);
      }
      return newSet;
    });
  };

  const handleDeleteClick = (briefingId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBriefingToDelete(briefingId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!briefingToDelete) return;

    try {
      // Soft delete via the delete-briefing edge function. We can't
      // do this as a direct UPDATE from the client because Postgres
      // applies an implicit post-write SELECT-visibility check on
      // UPDATE: setting deleted_at = now() makes the new row
      // invisible under the producer SELECT policy (which hides
      // soft-deleted rows for defense-in-depth), and PG rejects the
      // write with 42501. The edge function validates ownership via
      // JWT and writes with the service-role key, bypassing RLS for
      // this single mutation only. Child rows (festival_areas,
      // map_settings, crew_locations, crew_timeline_events,
      // crew_assignments, crew_acknowledgements, crew_members,
      // lineup, …) stay physically intact; restore is a single
      // UPDATE briefings SET deleted_at = NULL by an admin.
      const { error } = await supabase.functions.invoke("delete-briefing", {
        body: { briefingId: briefingToDelete },
      });

      if (error) throw error;

      toast({
        title: "Production deleted",
        description: "The production has been successfully deleted.",
      });

      queryClient.invalidateQueries({ queryKey: ["briefings"] });
    } catch (error) {
      console.error(error);
      // A revoked session (typically right after a password change) fails
      // here with a 401. "Please try again" is useless then — retrying can
      // never succeed until the user signs in again. endExpiredSession()
      // reports it and clears the dead session, so the guards route them to
      // the login page instead of the upgrade page.
      if (isAuthSessionError(error)) {
        await endExpiredSession();
      } else {
        toast({
          title: "Error",
          description: "Failed to delete production. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setDeleteDialogOpen(false);
      setBriefingToDelete(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AppSidebar />

      {/* Main Content */}
      <PageTransition
        className="flex-1 p-8 transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" } as React.CSSProperties}
      >
        {/* Banners */}
        <EmergencyModeBanner />
        <div className="mb-6 mt-4 space-y-4">
          {!isSubscriptionLoading &&
            subscriptionStatus?.plan_status === "trial" &&
            subscriptionStatus?.plan_tier &&
            subscriptionStatus?.trial_ends_at && (
              <TrialBanner
                planTier={subscriptionStatus.plan_tier}
                trialEndsAt={subscriptionStatus.trial_ends_at}
              />
            )}
          <VerificationReminderBanner />
          <IncompleteProfileBanner />
        </div>

        {/* Search */}
        <div className="mb-8" data-guide="search">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search for projects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-border bg-card pl-10"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-8">
            <div>
              <Skeleton className="mb-6 h-6 w-48" />
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-48 rounded-lg" />
                ))}
              </div>
            </div>
            <div>
              <Skeleton className="mb-4 h-6 w-32" />
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 rounded-lg" />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && allBriefings.length === 0 && (
          <div className="flex min-h-[500px] flex-col items-center justify-center text-center">
            <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-muted/50">
              <Calendar className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="mb-2 text-2xl font-semibold">No productions yet</h2>
            <p className="mb-8 max-w-md text-muted-foreground">
              Get started by creating your first production. Add festival details, crew schedules,
              and manage your entire production workflow.
            </p>
            <Link to="/briefings/create">
              <Button data-guide="create-production" size="lg" className="gap-2">
                <Plus className="h-5 w-5" />
                Create new production
              </Button>
            </Link>
          </div>
        )}

        {/* Recently Opened Productions */}
        {!isLoading && recentlyOpened.length > 0 && (
          <div className="mb-12">
            <h2 className="mb-6 text-xl font-semibold">Recently opened productions</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {/* Add New Project Card */}
              <Link to="/briefings/create">
                <Card
                  data-guide="create-production"
                  className="group flex aspect-[4/3] cursor-pointer flex-col items-center justify-center border-border bg-card transition-all hover:bg-card/80"
                >
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 transition-transform group-hover:scale-110">
                    <Plus className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium text-muted-foreground">Start new production</p>
                </Card>
              </Link>

              {/* Recent Briefing Cards */}
              {recentlyOpened.map((briefing) => (
                <Link
                  key={briefing.id}
                  to={
                    briefing.status === "draft"
                      ? `/briefings/${briefing.id}/edit?step=${briefing.last_edit_step || 0}`
                      : `/productions/${briefing.id}`
                  }
                >
                  <Card className="flex aspect-[4/3] cursor-pointer flex-col border-0 bg-primary p-6 text-primary-foreground transition-all hover:scale-105">
                    <h3 className="mb-2 text-lg font-bold">{briefing.festival_name}</h3>
                    <Badge
                      variant="secondary"
                      className={`mb-auto w-fit ${
                        briefing.status === "draft"
                          ? "border-orange-500/20 bg-orange-500/10 text-orange-600 dark:text-orange-400"
                          : "border-blue-700 bg-blue-600 text-white"
                      }`}
                    >
                      {briefing.status === "draft" ? "Draft" : "Published"}
                    </Badge>
                    <div className="mt-4 space-y-2 text-sm">
                      {briefing.festival_date_start && (
                        <p className="font-medium">
                          {formatDateRange(
                            briefing.festival_date_start,
                            briefing.festival_date_end,
                          )}
                        </p>
                      )}
                      {briefing.location && (
                        <p className="text-primary-foreground/80">{briefing.location}</p>
                      )}
                      {crewData[briefing.id] && (
                        <p className="text-primary-foreground/80">
                          {crewData[briefing.id]} crew members
                        </p>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Drafts Section */}
        {drafts.length > 0 && (
          <div className="mb-12">
            <Collapsible open={draftsOpen} onOpenChange={setDraftsOpen}>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Drafts</h2>
                {drafts.length > 4 && (
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      {draftsOpen ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          Collapse
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Expand
                        </>
                      )}
                    </Button>
                  </CollapsibleTrigger>
                )}
              </div>
              <CollapsibleContent>
                <div className="space-y-2">
                  {drafts.map((briefing: Briefing) => (
                    <Link
                      key={briefing.id}
                      to={`/briefings/${briefing.id}/edit?step=${briefing.last_edit_step || 0}`}
                    >
                      <Card className="flex cursor-pointer items-center gap-6 border-border bg-card p-4 transition-colors hover:bg-card/80">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold">{briefing.festival_name}</h3>
                          {briefing.location && (
                            <p className="text-sm text-muted-foreground">{briefing.location}</p>
                          )}
                        </div>

                        {briefing.festival_date_start && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {formatDateRange(
                                briefing.festival_date_start,
                                briefing.festival_date_end,
                              )}
                            </span>
                          </div>
                        )}

                        <Badge
                          variant="secondary"
                          className="border-orange-500/20 bg-orange-500/10 text-orange-600 dark:text-orange-400"
                        >
                          Draft
                        </Badge>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          onClick={(e) => handleDeleteClick(briefing.id, e)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </Card>
                    </Link>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Published Productions */}
        {published.length > 0 && (
          <div>
            <Collapsible open={publishedOpen} onOpenChange={setPublishedOpen}>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Published productions</h2>
                {published.length > 4 && (
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2">
                      {publishedOpen ? (
                        <>
                          <ChevronUp className="h-4 w-4" />
                          Collapse
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" />
                          Expand
                        </>
                      )}
                    </Button>
                  </CollapsibleTrigger>
                )}
              </div>
              <CollapsibleContent>
                <div className="space-y-2">
                  {published.map((briefing) => {
                    const isExpanded = expandedRows.has(briefing.id);

                    return (
                      <Card
                        key={briefing.id}
                        className="border-border bg-card p-4 transition-colors hover:bg-card/80"
                      >
                        <div className="flex items-center justify-between gap-4">
                          {/* Left side - Production info */}
                          <div className="min-w-0 flex-1">
                            <h3 className="mb-1 font-semibold">{briefing.festival_name}</h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                              {briefing.location && <span>{briefing.location}</span>}
                              {briefing.festival_date_start && (
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-3.5 w-3.5" />
                                  <span>
                                    {formatDateRange(
                                      briefing.festival_date_start,
                                      briefing.festival_date_end,
                                    )}
                                  </span>
                                </div>
                              )}
                              {crewData[briefing.id] && (
                                <span>{crewData[briefing.id]} crew members</span>
                              )}
                            </div>
                          </div>

                          {/* Right side - Status badge and action buttons */}
                          <div className="flex flex-shrink-0 items-center gap-3">
                            <Badge className="bg-primary/20 text-primary hover:bg-primary/30">
                              Published
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/productions/${briefing.id}`)}
                              aria-label="Open Production"
                            >
                              Open
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => toggleRowExpanded(briefing.id)}
                              aria-label="Edit Production"
                            >
                              <Edit className="h-4 w-4" />
                              Edit
                            </Button>
                          </div>
                        </div>

                        {/* Expandable quick actions with animation */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: "easeOut" }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=0&shortcut=true`)
                                  }
                                  aria-label="Edit Days"
                                >
                                  <CalendarRange className="h-3.5 w-3.5" />
                                  Days
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=1&shortcut=true`)
                                  }
                                  aria-label="Edit Stages"
                                >
                                  <Layers className="h-3.5 w-3.5" />
                                  Stages
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=2&shortcut=true`)
                                  }
                                  aria-label="Edit Timetable"
                                >
                                  <Clock className="h-3.5 w-3.5" />
                                  Timetable
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=3&shortcut=true`)
                                  }
                                  aria-label="Edit Crew"
                                >
                                  <Users className="h-3.5 w-3.5" />
                                  Crew
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=4&shortcut=true`)
                                  }
                                  aria-label="Edit Festival Map"
                                >
                                  <MapPin className="h-3.5 w-3.5" />
                                  Festival Map
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=5&shortcut=true`)
                                  }
                                  aria-label="Edit Schedule"
                                >
                                  <CalendarCheck className="h-3.5 w-3.5" />
                                  Schedule
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=6&shortcut=true`)
                                  }
                                  aria-label="Edit Briefing"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                  Briefing
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() =>
                                    navigate(`/briefings/${briefing.id}/edit?step=7&shortcut=true`)
                                  }
                                  aria-label="Live tracking setup"
                                >
                                  <Radio className="h-3.5 w-3.5" />
                                  Live tracking
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2 text-destructive hover:text-destructive"
                                  onClick={(e) => handleDeleteClick(briefing.id, e)}
                                  aria-label="Delete Production"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete
                                </Button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </PageTransition>

      {/* Delete Confirmation Dialog */}
      <DeleteProductionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        productionName={allBriefings.find((b) => b.id === briefingToDelete)?.festival_name}
      />

      {/* Onboarding Guide */}
      {showGuide && (
        <OnboardingGuide
          steps={dashboardGuideSteps}
          onComplete={handleGuideComplete}
          onSkip={handleGuideComplete}
        />
      )}
    </div>
  );
};

export default Dashboard;
